import React from 'react';
import { Line } from 'react-chartjs-2';
import { formatCompactNumber } from '../utils/formatters';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

const SERIES = [
  { key: 'users', label: 'Users', color: '#3491f7', axis: 'y' },
  { key: 'books', label: 'Books', color: '#f5a623', axis: 'y' },
  { key: 'pages', label: 'Pages', color: '#8e44ad', axis: 'y1' }
];

// Hide point markers when there are too many days to keep the lines readable
const MAX_POINTS_WITH_MARKERS = 45;

const DailyReadingsChart = ({ data }) => {
  const showPoints = data.length <= MAX_POINTS_WITH_MARKERS;

  const chartData = {
    labels: data.map(item => item.date),
    datasets: SERIES.map(series => ({
      label: series.label,
      data: data.map(item => item[series.key]),
      yAxisID: series.axis,
      borderColor: series.color,
      backgroundColor: series.color,
      borderWidth: 2.5,
      pointBackgroundColor: series.color,
      pointBorderColor: '#fff',
      pointBorderWidth: 1.5,
      pointRadius: showPoints ? 3 : 0,
      pointHoverRadius: 6,
      fill: false,
      tension: 0.3
    }))
  };

  const axisTicks = {
    color: '#818181',
    font: { size: 12 },
    callback: (value) => formatCompactNumber(value)
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: 'index',
      intersect: false
    },
    plugins: {
      legend: {
        position: 'top',
        align: 'end',
        labels: {
          color: '#818181',
          usePointStyle: true,
          pointStyle: 'rectRounded',
          boxWidth: 10,
          boxHeight: 10,
          padding: 16,
          font: { size: 12, weight: '600' }
        }
      },
      title: {
        display: false
      },
      tooltip: {
        backgroundColor: 'rgba(51, 51, 51, 0.95)',
        padding: 12,
        titleColor: '#fff',
        bodyColor: '#fff',
        borderColor: '#3491f7',
        borderWidth: 1,
        usePointStyle: true,
        callbacks: {
          title: (tooltipItems) => {
            const label = tooltipItems[0].label;
            if (/^\d{4}-\d{2}-\d{2}$/.test(label)) {
              const dayName = new Date(label + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'long' });
              return [label, dayName];
            }
            return label;
          },
          label: (context) => `${context.dataset.label}: ${context.parsed.y.toLocaleString()}`
        }
      }
    },
    scales: {
      y: {
        type: 'linear',
        position: 'left',
        beginAtZero: true,
        title: {
          display: true,
          text: 'Users / Books',
          color: '#818181',
          font: { size: 12, weight: '600' }
        },
        ticks: axisTicks,
        grid: {
          color: 'rgba(129, 129, 129, 0.1)',
          drawBorder: false
        }
      },
      y1: {
        type: 'linear',
        position: 'right',
        beginAtZero: true,
        title: {
          display: true,
          text: 'Pages',
          color: '#818181',
          font: { size: 12, weight: '600' }
        },
        ticks: axisTicks,
        grid: {
          drawOnChartArea: false
        }
      },
      x: {
        ticks: {
          color: '#818181',
          font: { size: 12 },
          maxRotation: 45,
          autoSkip: true,
          maxTicksLimit: 16
        },
        grid: {
          display: false
        }
      }
    },
    animation: {
      duration: 1000,
      easing: 'easeInOutQuart'
    }
  };

  return (
    <div className="data-page-chart-card chart-above-table">
      <div className="chart-header">
        <h3>Daily Readings</h3>
        <span className="chart-badge">
          {formatCompactNumber(data.length)} days
        </span>
      </div>
      <div className="chart-wrapper">
        <Line data={chartData} options={options} />
      </div>
    </div>
  );
};

export default DailyReadingsChart;
